import React from "react";
import { DataGrid, GridColDef, GridActionsCellItem, GridToolbarContainer } from '@mui/x-data-grid';
import EditIcon from '@mui/icons-material/Edit';
import {Button} from "@mui/material";
import {Box} from "@mui/joy";
import { Experimental_CssVarsProvider as CssVarsProvider } from "@mui/material/styles";
import IServiceOrder from "../../../types/service-order.type";
import IWorkContact from "../../../types/work-contact.type";
import AddIcon from "@mui/icons-material/Add";
import WorkContactModal from "./WorkContactModal";
import ConfirmationModal from "../../utils/ConfirmationModal";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import {v4 as uuidv4} from "uuid";
import VisibilityIcon from "@mui/icons-material/Visibility";

interface Props {
  serviceOrder: IServiceOrder;
  setServiceOrder: (updatedServiceOrder: IServiceOrder | ((prev: IServiceOrder) => IServiceOrder)) => void;
  readOnly?: boolean;
}

const ServiceOrdersList: React.FC<Props> = ({serviceOrder, setServiceOrder, readOnly = false}) => {
  const [modalOpen, setModalOpen] = React.useState(false);
  const [confirmationModalOpen, setConfirmationModalOpen] = React.useState(false);
  const [selectedWorkContact, setSelectedWorkContact] = React.useState<IWorkContact>();

  const handleConfirmationModalClose = () => {
    setSelectedWorkContact(undefined);
    setConfirmationModalOpen(false);
  }

  const handleDeleteClick = (workContact: IWorkContact) => () => {
    setSelectedWorkContact(workContact);
    setConfirmationModalOpen(true);
  };

  const onConfirm = () => {
    setServiceOrder(prev => ({
      ...prev,
      workContacts: prev.workContacts.filter(ws => ws.id !== selectedWorkContact?.id),
    }));

    setSelectedWorkContact(undefined);
    setConfirmationModalOpen(false);
  };

  const handleModalClose = () => {
    setSelectedWorkContact(undefined);
    setModalOpen(false);
  }

  const handleAddClick = () => {
    setSelectedWorkContact(undefined);
    setModalOpen(true);
  };

  const handleEditClick = (workContact: IWorkContact) => () => {
    setSelectedWorkContact(workContact);
    setModalOpen(true);
  };

  const onSubmit = (workContact: IWorkContact) => {
    const id =
      workContact.id
      ?? globalThis.crypto?.randomUUID?.()
      ?? uuidv4();

    const newContact = {
      ...workContact,
      id: id,
    };

    setServiceOrder(prev => ({
      ...prev,
      workContacts: prev.workContacts.some(contact => contact.id === newContact.id)
        ? prev.workContacts.map(contact => contact.id === newContact.id ? newContact : contact)
        : [...prev.workContacts, newContact],
    }));

    setSelectedWorkContact(undefined);
    setModalOpen(false);
  };

  const columns: GridColDef<IWorkContact>[] = [
    {
      field: 'firstName',
      headerName: 'Nombre',
      filterable: false,
      headerAlign: 'center',
      align: 'center',
      minWidth:160,
      flex: 1
    },
    {
      field: 'lastName',
      headerName: 'Apellido',
      filterable: false,
      headerAlign: 'center',
      align: 'center',
      minWidth:160,
      flex: 1
    },
    {
      field: 'email',
      headerName: 'Email',
      filterable: false,
      headerAlign: 'center',
      align: 'center',
      minWidth:160,
      flex: 1
    },
    {
      field: 'principalPhone',
      headerName: 'Tel. Principal',
      filterable: false,
      headerAlign: 'center',
      align: 'center',
      minWidth:160,
      flex: 1
    },
    {
      field: 'secondaryPhone',
      headerName: 'Tel. Secundario',
      filterable: false,
      headerAlign: 'center',
      align: 'center',
      minWidth:160,
      flex: 1
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Acciones',
      headerAlign: 'center',
      align: 'center',
      minWidth: 20,
      flex: 1,
      cellClassName: 'actions',
      getActions: (params) => {
        if (readOnly){
          return [
            <GridActionsCellItem
              icon={<VisibilityIcon/>}
              label="Edit"
              className="textPrimary"
              onClick={handleEditClick(params.row)}
              color="inherit"
            />
          ];
        } else {
          return [
            <GridActionsCellItem
              icon={<EditIcon/>}
              label="Edit"
              className="textPrimary"
              onClick={handleEditClick(params.row)}
              color="inherit"
            />,
            <GridActionsCellItem
              icon={<DeleteForeverIcon/>}
              label="Delete"
              className="textPrimary"
              onClick={handleDeleteClick(params.row)}
              color="inherit"
            />
          ];
        }
      },
    },
  ];

  function EditToolbar() {
    return (
      <GridToolbarContainer sx={{paddingBottom: 1}}>
        <Box sx={{ flexGrow: 1 }} />
        <Button
          variant="outlined"
          endIcon={<AddIcon />}
          onClick={handleAddClick}
          disabled={readOnly}
        >
          Agregar Contacto de Obra
        </Button>
      </GridToolbarContainer>
    );
  }

  return (
    <Box sx={{ width: '100%', height: 384, typography: 'body1' }} mt={1}>

      <ConfirmationModal
        open={confirmationModalOpen}
        onConfirm={onConfirm}
        onCancel={handleConfirmationModalClose} />

      <WorkContactModal
        isOpen={modalOpen}
        onSubmit={onSubmit}
        onClose={handleModalClose}
        workContact={selectedWorkContact}
        readOnly={readOnly}
      />

      <CssVarsProvider>
        <DataGrid
          slots={{toolbar: EditToolbar}}
          rows={serviceOrder.workContacts}
          columns={columns}
          rowHeight={25}
          columnHeaderHeight={30}
          initialState={{
            pagination: {
              paginationModel: {
                pageSize: 10,
              },
            },
          }}
          pageSizeOptions={[10]}
          localeText={{ noRowsLabel: "No Hay Contactos de Obra" }}
          disableRowSelectionOnClick
        />

      </CssVarsProvider>
    </Box>
  );
};

export default ServiceOrdersList;
